import React, { useEffect, useRef } from 'react';
import { Coordinates } from '../TimeIn';

interface LocationMapProps {
  currentLocation: Coordinates | null;
  selectedLocation: Coordinates | null;
  radius: number;
  isLoading: boolean;
  onLocationSelect: (location: Coordinates) => void;
  onMarkerDrag: (location: Coordinates) => void;
}

const LocationMap: React.FC<LocationMapProps> = ({
  currentLocation,
  selectedLocation,
  radius,
  isLoading,
  onLocationSelect,
  onMarkerDrag
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<any>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const currentLocationMarkerRef = useRef<any>(null);

  const updateCurrentLocationMarker = (location: Coordinates) => {
    if (!mapRef.current) {
      console.warn('Cannot update current location marker: map not initialized');
      return;
    }

    try {
      // Remove existing marker if it exists
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.map = null;
        currentLocationMarkerRef.current = null;
      }

      // Create new current location marker with higher z-index
      const markerView = new google.maps.marker.PinElement({
        background: '#4285F4',
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.5  // Make it slightly larger to be more visible
      });

      currentLocationMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position: location,
        title: 'Current Location',
        content: markerView.element,
        zIndex: 1000  // Higher z-index to ensure it appears above red marker
      });

      console.log('Current location marker created at:', location);
    } catch (error) {
      console.error('Error creating current location marker:', error);
    }
  };

  // Adjust map bounds to show both current location and selected location with radius
  const adjustMapBounds = () => {
    if (!mapRef.current) return;

    const bounds = new google.maps.LatLngBounds();

    // Always add current location to bounds if available (prioritize showing it)
    if (currentLocation) {
      bounds.extend(new google.maps.LatLng(currentLocation.lat, currentLocation.lng));
      // Add a small buffer around current location to ensure marker is visible
      const buffer = 0.0001; // ~11 meters
      bounds.extend(new google.maps.LatLng(currentLocation.lat + buffer, currentLocation.lng + buffer));
      bounds.extend(new google.maps.LatLng(currentLocation.lat - buffer, currentLocation.lng - buffer));
    }

    // Add selected location and radius circle to bounds if available
    if (selectedLocation && circleRef.current) {
      const circleBounds = circleRef.current.getBounds();
      if (circleBounds) {
        bounds.union(circleBounds);
      } else {
        bounds.extend(new google.maps.LatLng(selectedLocation.lat, selectedLocation.lng));
      }
    } else if (selectedLocation) {
      bounds.extend(new google.maps.LatLng(selectedLocation.lat, selectedLocation.lng));
    }

    // Only adjust bounds if we have at least one location
    if (!bounds.isEmpty()) {
      // Add padding to ensure markers are visible, especially current location
      mapRef.current.fitBounds(bounds, {
        top: 100,
        right: 100,
        bottom: 100,
        left: 100
      });
    } else if (currentLocation) {
      // If only current location is available, center on it with good zoom
      mapRef.current.setCenter(currentLocation);
      mapRef.current.setZoom(17); // Higher zoom to ensure marker is clearly visible
    }
  };

  // Initialize map and show current location marker
  useEffect(() => {
    if (!window.google?.maps) return;

    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    try {
      if (!mapRef.current) {
        // Use currentLocation if available, otherwise use a default center (Manila, Philippines)
        const defaultCenter: Coordinates = { lat: 14.5995, lng: 120.9842 };
        const initialCenter = currentLocation || defaultCenter;

        const map = new google.maps.Map(mapElement, {
          center: initialCenter,
          zoom: currentLocation ? 15 : 10,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
          zoomControl: true,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapId: '8f52cca0c6d4e074',  // Valid Map ID for Advanced Markers
          gestureHandling: 'greedy'
        });

        mapRef.current = map;

        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          const latLng = e.latLng;
          if (latLng) {
            const clickedLocation = {
              lat: latLng.lat(),
              lng: latLng.lng()
            };
            onLocationSelect(clickedLocation);
            updateMarkers(clickedLocation, map);
          }
        });

        // Show current location marker immediately if available on map initialization
        if (currentLocation) {
          console.log('Map initialized, creating current location marker:', currentLocation);
          updateCurrentLocationMarker(currentLocation);
        }
      }

      // Always update current location marker when it changes (including initial load)
      if (currentLocation && mapRef.current) {
        console.log('Updating current location marker on map change:', currentLocation);
        updateCurrentLocationMarker(currentLocation);
        adjustMapBounds();
      }

    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, [currentLocation, onLocationSelect]);

  // Update current location marker whenever currentLocation changes
  useEffect(() => {
    if (!mapRef.current) {
      console.log('Map not ready, waiting for map initialization');
      return;
    }
    
    if (!currentLocation) {
      console.log('No current location available');
      return;
    }

    console.log('Updating current location marker:', currentLocation);
    updateCurrentLocationMarker(currentLocation);
    adjustMapBounds();
  }, [currentLocation]);

  // Update markers when selected location or radius changes
  useEffect(() => {
    if (!mapRef.current || !selectedLocation) return;
    updateMarkers(selectedLocation, mapRef.current);
    adjustMapBounds();
  }, [selectedLocation, radius]);

  const updateMarkers = (location: Coordinates, map: google.maps.Map) => {
    // Don't cleanup current location marker - we want to keep it visible
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    // Create new marker for selected location
    const markerView = new google.maps.marker.PinElement({
      background: '#FF0000',
      borderColor: '#FFFFFF',
      glyphColor: '#FFFFFF',
      scale: 1.2
    });

    // Create or update the radius circle
    circleRef.current = new google.maps.Circle({
      strokeColor: '#3498db',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#3498db',
      fillOpacity: 0.15,
      map: map,
      center: location,
      radius: radius,
      clickable: false,
      zIndex: 1
    });

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: location,
      map: map,
      title: 'Work Location',
      content: markerView.element,
      gmpDraggable: true,
      zIndex: 100  // Lower z-index than current location marker
    });

    markerRef.current = marker;

    marker.addEventListener('dragend', () => {
      const newPos = marker.position;
      if (newPos) {
        const draggedLocation = {
          lat: typeof newPos.lat === 'function' ? newPos.lat() : newPos.lat,
          lng: typeof newPos.lng === 'function' ? newPos.lng() : newPos.lng
        };
        onMarkerDrag(draggedLocation);
        if (circleRef.current) {
          circleRef.current.setCenter(newPos);
        }
        adjustMapBounds();
      }
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.map = null;
        currentLocationMarkerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="map-container">
      <div id="map" style={{ width: '100%', height: '400px' }}>
        {isLoading && (
          <div className="map-loading">
            <div className="loading-spinner"></div>
            <p>Loading map...</p>
          </div>
        )}
      </div>
      {selectedLocation && (
        <div className="selected-location-info">
          <p>Selected Location: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}</p>
        </div>
      )}
    </div>
  );
};

export default LocationMap;

